import { Stack, Typography, useTheme } from "@mui/material";
import { Button, CheckCircleIcon, Drawer, SearchInput } from "components";
import { useUpdateCustomerNotificationsSettings } from "controller";
import {
  useUpdateMspResourceNotificationSettings,
  useUpdateMspUserNotificationSettings,
} from "controller/hooks/control-center/notifications";
import { ReactElement, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { debugConsole } from "utilz/helpers";

export const NotifyDrawer = ({
  open,
  onClose,
  notifyMethodIcon,
  notifyMethodLabel,
  onChange: onSelectedRolesChange,
  value: selectedRoles = [],
  roleTypes,
  isRoleTypesDataLoading,
  searchQuery,
  setSearchQuery,
  isChatBased,
  formFieldName,
}: {
  open: boolean;
  onClose: () => void;
  notifyMethodIcon: ReactElement;
  notifyMethodLabel: string;
  onChange: (value: any[]) => void;
  value: any[];
  roleTypes: any[];
  isRoleTypesDataLoading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isChatBased: boolean;
  formFieldName: string;
}) => {
  const theme = useTheme();
  const { id: customerId } = useParams();

  const {
    mutateAsync: updateMspResourceNotificationSettings,
    isLoading: isUpdateMspResourceNotificationSettingsLoading,
  } = useUpdateMspResourceNotificationSettings();
  const {
    mutateAsync: updateMspUserNotificationSettings,
    isLoading: isUpdateMspUserNotificationSettingsLoading,
  } = useUpdateMspUserNotificationSettings();
  const {
    mutateAsync: updateCustomerNotificationsSettings,
    isLoading: isUpdateCustomerNotificationsSettingsLoading,
  } = useUpdateCustomerNotificationsSettings();

  const isUpdateLoading =
    isUpdateMspResourceNotificationSettingsLoading ||
    isUpdateMspUserNotificationSettingsLoading ||
    isUpdateCustomerNotificationsSettingsLoading;

  const [selectedRolesTemporary, setSelectedRolesTemporary] = useState(
    selectedRoles ?? [],
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isTurnOffNotificationsClicked, setIsTurnOffNotificationsClicked] =
    useState(false);

  // Function to compare arrays irrespective of order
  const areArraysEqual = (arr1: any[], arr2: any[]) => {
    if (arr1?.length !== arr2?.length) return false;
    const set1 = new Set(arr1.map((obj) => obj?.role_id));
    const set2 = new Set(arr2.map((obj) => obj?.role_id));
    return (
      arr1.every((obj) => set2.has(obj?.role_id)) &&
      arr2.every((obj) => set1.has(obj?.role_id))
    );
  };

  useEffect(() => {
    setSelectedRolesTemporary(selectedRoles);
  }, [selectedRoles]);

  useEffect(() => {
    setIsDirty(!areArraysEqual(selectedRoles, selectedRolesTemporary));
  }, [selectedRoles, selectedRolesTemporary]);

  const getRoleLabel = (role_name: string) => {
    switch (role_name) {
      case "chat_members":
        return "Chat Members";
      case "manager_1":
        return "Manager";
      case "customer_user":
        return "User";
      default:
        return role_name;
    }
  };

  const personnelType = formFieldName?.split(".")?.[0];
  const ALL_ROLE_ID = "fcbd88f6-dec3-404b-8cd0-425a845d0766";
  const otherRoles = [
    ...(personnelType === "resources"
      ? [
          { role_id: ALL_ROLE_ID, role_name: "all" },
          {
            role_id: "560462c2-6590-4be5-91d6-5ac74421ac09",
            role_name: "assignees",
          },
        ]
      : []),
    ...(isChatBased
      ? [
          {
            role_id: "d0479bf2-ca1f-4ce6-9db3-cdb397df3178",
            role_name: "chat_members",
          },
        ]
      : []),
  ].filter((role) =>
    getRoleLabel(role.role_name)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const isAllSelected =
    selectedRolesTemporary?.some(
      (selectedRole) => selectedRole?.role_id === ALL_ROLE_ID,
    ) ?? false;
  const getIsSelected = (currentRoleId: string) => {
    if (isAllSelected) return true;
    return (
      selectedRolesTemporary?.some(
        (selectedRole) => selectedRole?.role_id === currentRoleId,
      ) ?? false
    );
  };

  const onMenuItemClick = (currentRoleObj: {
    role_id: string;
    role_name: string;
  }) => {
    let newSelectedRolesTemporary = [];
    const isCurrentRoleSelected = getIsSelected(currentRoleObj?.role_id);
    if (isCurrentRoleSelected) {
      if (isAllSelected && currentRoleObj?.role_id !== ALL_ROLE_ID) {
        newSelectedRolesTemporary = [
          ...(otherRoles?.filter(
            (item) =>
              item?.role_id !== ALL_ROLE_ID &&
              item?.role_id !== currentRoleObj?.role_id,
          ) || []),
          ...(roleTypes?.filter(
            (item: any) => item?.role_id !== currentRoleObj?.role_id,
          ) || []),
        ];
      } else {
        newSelectedRolesTemporary =
          selectedRolesTemporary?.filter(
            (selectedRole) => selectedRole?.role_id !== currentRoleObj.role_id,
          ) || [];
      }
    } else {
      if (currentRoleObj?.role_id === ALL_ROLE_ID) {
        newSelectedRolesTemporary = [currentRoleObj];
      } else {
        newSelectedRolesTemporary = [
          ...(selectedRolesTemporary || []),
          currentRoleObj,
        ];
      }
    }
    // onSelectedRolesChange(newSelectedRolesTemporary);
    setSelectedRolesTemporary(newSelectedRolesTemporary);
  };

  const resetAndClose = () => {
    onClose();
    setSelectedRolesTemporary(selectedRoles ?? []);
  };

  const { handleSubmit } = useFormContext();

  const formFieldNameSplitArr = formFieldName.split(".");
  const notificationType =
    formFieldNameSplitArr[formFieldNameSplitArr.length - 2];
  const getFormDataForCurrentNotificationType = (formData: any) => {
    // if say, formFieldName = "resources.ticket.new_ticket.web_app" -> it gets value of formData.resources.ticket.new_ticket
    // this is done because - even though the drawer has only say, web_app's selected roles, the update api requires data of all mediums of the specific notification type - like email, mobile etc
    return formFieldNameSplitArr
      .slice(0, -1)
      .reduce((acc, key) => acc?.[key], formData);
  };

  const onSubmit = async (formData: any) => {
    const data = getFormDataForCurrentNotificationType(formData);
    const getIsEnabled = () => {
      if (!data) return false;
      return [
        "web_app",
        "email",
        "resource_mobile_app",
        "agent_app_desktop",
        "agent_mobile_app",
        "teams_app",
      ].some((field) => data[field]?.length > 0);
    };
    const isEnabled = getIsEnabled();
    const finalData = {
      notification_status: isEnabled ? true : false,
      notification_sound: isEnabled ? data?.notification_sound : false,
      notify_methods: [
        ...(data?.web_app?.length > 0
          ? [
              {
                notify_through: "web_app",
                roles: data?.web_app,
              },
            ]
          : []),
        ...(data?.email?.length > 0
          ? [
              {
                notify_through: "email",
                roles: data?.email,
              },
            ]
          : []),
        ...(data?.resource_mobile_app?.length > 0
          ? [
              {
                notify_through: "resource_mobile_app",
                roles: data?.resource_mobile_app,
              },
            ]
          : []),
        ...(data?.agent_app_desktop?.length > 0
          ? [
              {
                notify_through: "agent_app_desktop",
                roles: data?.agent_app_desktop,
              },
            ]
          : []),
        ...(data?.agent_mobile_app?.length > 0
          ? [
              {
                notify_through: "agent_mobile_app",
                roles: data?.agent_mobile_app,
              },
            ]
          : []),
        ...(data?.teams_app?.length > 0
          ? [
              {
                notify_through: "teams_app",
                roles: data?.teams_app,
              },
            ]
          : []),
      ],
    };
    try {
      const resData =
        personnelType === "users"
          ? await updateMspUserNotificationSettings({
              data: finalData,
              notificationType,
            })
          : personnelType === "customers"
            ? await updateCustomerNotificationsSettings({
                data: finalData,
                customerId: customerId || "",
                notificationType,
              })
            : await updateMspResourceNotificationSettings({
                data: finalData,
                notificationType,
              });
      toast.success("Updated notification settings successfully");
      onClose();
    } catch (error) {
      toast.error("Failed to update notification settings");
    }
  };
  const onFormError = (error: any) => debugConsole("form submit error", error);

  return (
    <Drawer
      open={open}
      title="Notify"
      onClose={resetAndClose}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={resetAndClose}
      primaryButtonText="Save"
      onPrimaryButtonClick={() => {
        onSelectedRolesChange(selectedRolesTemporary);
        handleSubmit(onSubmit, onFormError)();
      }}
      drawerStyle={{
        minWidth: "342px",
      }}
      {...(!isDirty &&
        !isUpdateLoading && {
          footer: <></>,
        })}
      isLoading={isUpdateLoading}
    >
      <Stack height="100%">
        <Stack gap="8px">
          {selectedRoles?.length > 0 && (
            <Stack
              direction="row"
              alignItems={"center"}
              justifyContent={"space-between"}
              p="5px 16px"
              bgcolor="#FFF4CB"
              boxShadow={"2px 2px 6px 0px rgba(0, 0, 0, 0.12)"}
            >
              <Stack direction="row" alignItems={"center"} gap="4px">
                {notifyMethodIcon}
                <Typography variant="body-medium">
                  {notifyMethodLabel}
                </Typography>
              </Stack>
              <Button
                variant="secondary"
                onClick={async () => {
                  setIsTurnOffNotificationsClicked(true);
                  onSelectedRolesChange([]);
                  try {
                    await handleSubmit(onSubmit, onFormError)();
                    setIsTurnOffNotificationsClicked(false);
                    setSelectedRolesTemporary([]);
                  } catch (error) {
                    onSelectedRolesChange(selectedRolesTemporary);
                    setIsTurnOffNotificationsClicked(false);
                  }
                }}
                loading={isTurnOffNotificationsClicked && isUpdateLoading}
              >
                Turn Off Notification
              </Button>
            </Stack>
          )}
          <Stack p="8px 16px">
            <SearchInput
              name="role-search"
              placeholder="Search"
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              value={searchQuery}
            />
          </Stack>
        </Stack>
        <Stack
          sx={{
            overflowY: "auto",
          }}
        >
          {isRoleTypesDataLoading ? (
            <></>
          ) : (
            <>
              {otherRoles?.map(({ role_id, role_name }) => {
                const roleObj = {
                  role_id,
                  role_name,
                };
                const isSelected = getIsSelected(role_id);
                const bg = isSelected
                  ? theme.palette.primitive.brand.purple[25]
                  : "transparent";
                const hoverBg = isSelected
                  ? theme.palette.primitive.brand.purple[25]
                  : theme.palette.neutral["010"];
                return (
                  <Stack
                    key={role_name}
                    direction="row"
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    p="10px 16px"
                    sx={{
                      cursor: "pointer",
                      bgcolor: bg,
                      ":hover": {
                        bgcolor: hoverBg,
                      },
                    }}
                    onClick={() => onMenuItemClick(roleObj)}
                  >
                    <Typography
                      variant="body-medium"
                      textTransform={"capitalize"}
                    >
                      {getRoleLabel(role_name)}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        htmlColor={theme.palette.primitive.brand.purple[700]}
                        style={{ width: "20px", height: "20px" }}
                      />
                    )}
                  </Stack>
                );
              })}
              {roleTypes?.map((roleType: any) => {
                const isSelected = getIsSelected(roleType?.role_id);
                const bg = isSelected
                  ? theme.palette.primitive.brand.purple[25]
                  : "transparent";
                const hoverBg = isSelected
                  ? theme.palette.primitive.brand.purple[25]
                  : theme.palette.neutral["010"];
                return (
                  <Stack
                    key={roleType?.role_id}
                    direction="row"
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    p="10px 16px"
                    sx={{
                      cursor: "pointer",
                      bgcolor: bg,
                      ":hover": {
                        bgcolor: hoverBg,
                      },
                    }}
                    onClick={() => onMenuItemClick(roleType)}
                  >
                    <Typography
                      variant="body-medium"
                      textTransform={"capitalize"}
                    >
                      {getRoleLabel(roleType?.role_name)}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        htmlColor={theme.palette.primitive.brand.purple[700]}
                        style={{ width: "20px", height: "20px" }}
                      />
                    )}
                  </Stack>
                );
              })}
            </>
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
};
